import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";

import { useAuth } from "./context/AuthContext";

/* -------------------- Providers -------------------- */
import AppProviders from "./providers/AppProviders";

/* -------------------- Guards -------------------- */
import CompanyGate from "./components/CompanyGate";

/* -------------------- Layout -------------------- */
import DashboardLayout from "./components/DashboardLayout";

/* -------------------- Pages -------------------- */
import LoginPage from "./pages/LoginPage";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailsPage from "./pages/ProjectDetailsPage"; // ✅ FIXED
import Notifications from "./pages/Notifications";
import CreateCompanyPage from "./pages/CreateCompanyPage";
import TeamsPage from "./pages/TeamsPage";
import TeamDetailsPage from "./pages/TeamDetailsPage";
import Billing from "./pages/Billing";
import Reports from "./pages/ReportsPage";
import SelectCompanyPage from "./pages/SelectCompanyPage";
import ContractorsPage from "./pages/ContractorsPage";
import UserReportsPage from "./pages/UserReportsPage";
import TeamReportsPage from "./pages/TeamReportsPage";
/* Static */
import AboutUs from "./pages/about_us";
import ContactUs from "./pages/contact_us";
import PrivacyPolicy from "./pages/privacy_policy";
import RefundCancellation from "./pages/refund_cancellation";
import TermsOfService from "./pages/terms_of_service";

/* ===================================================== */
/* 🔐 Auth Guard                                          */
/* ===================================================== */

function RequireAuth() {
  const { isLoggedIn, loading } = useAuth();

  if (loading) return <FullPageLoader />;

  return isLoggedIn ? <Outlet /> : <Navigate to="/login" replace />;
}

function RedirectIfAuthed({ children }) {
  const { isLoggedIn, loading } = useAuth();

  if (loading) return <FullPageLoader />;

  return isLoggedIn ? <Navigate to="/dashboard" replace /> : children;
}

/* ===================================================== */
/* 🧱 Loader                                              */
/* ===================================================== */

function FullPageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center text-gray-500">
      Loading…
    </div>
  );
}

/* ===================================================== */
/* 🚀 App                                                 */
/* ===================================================== */

export default function App() {
  return (
    <Router>
      <AppProviders> {/* ✅ CLEAN PROVIDER WRAPPER */}

        <Routes>

          {/* ================= PUBLIC ================= */}

          <Route
            path="/login"
            element={
              <RedirectIfAuthed>
                <LoginPage />
              </RedirectIfAuthed>
            }
          />

          <Route
            path="/register"
            element={
              <RedirectIfAuthed>
                <Register />
              </RedirectIfAuthed>
            }
          />

          {/* Static */}
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/refund-cancellation" element={<RefundCancellation />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />

          {/* ================= AUTH ================= */}

          <Route element={<RequireAuth />}>

            {/* Onboarding (NO CompanyGate) */}
            <Route path="/company/create" element={<CreateCompanyPage />} />
            <Route path="/select-company" element={<SelectCompanyPage />} />

            {/* App (WITH CompanyGate) */}
            <Route
              element={
                <CompanyGate>
                  <DashboardLayout />
                </CompanyGate>
              }
            >
              {/* Dashboard */}
              <Route path="/dashboard" element={<Dashboard />} />

              {/* Projects */}
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/projects/new" element={<ProjectsPage openCreate />} />
              <Route path="/projects/:id" element={<ProjectDetailsPage />} /> {/* ✅ FIX */}

              {/* Teams */}
              <Route path="/teams" element={<TeamsPage />} />
              <Route path="/teams/:id" element={<TeamDetailsPage />} />

              {/* Billing */}
              <Route path="/billing" element={<Billing />} />

              {/* Notifications */}
              <Route path="/notifications" element={<Notifications />} />

              {/* Reports */}
              <Route path="/reports" element={<Reports />} />
              <Route path="/user-reports" element={<UserReportsPage/>}/>
              <Route path="/team-reports" element={<TeamReportsPage/>}/>
              
              {/* Contractors */}
              <Route path="/contractors" element={<ContractorsPage />} />
              {/* Default */}
              <Route index element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Route>

          {/* Legacy redirect */}
          <Route
            path="/create-company"
            element={<Navigate to="/company/create" replace />}
          />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />

        </Routes>

      </AppProviders>
    </Router>
  );
}